import { faTrashCan } from "@fortawesome/free-solid-svg-icons";
import { Icon } from "../../Icon";
import { tipAttrs } from "../../tooltip";
import { type Room, type RoomRect, roomFloorCells, roomVolume } from "@lancer-vtt/domain";
import type { EncounterDispatch } from "../StatusEditor";
import { NameField } from "./fields";

/**
 * The **room inspector** body — the room twin of {@link EditInspectorBody},
 * opened by double-clicking a rendered room's glowing floor. It edits a purely-visual {@link Room}
 * through the EXISTING commands only (no new command):
 *
 * - **rename** → `set_room` with the same id + rects and the edited name (replace-by-id);
 * - **per-rect ✕** → `set_room` with that rect dropped from the list — UNLESS it's the room's last
 * rect, in which case it's a `remove_room` (a zero-rect room is meaningless, so dropping the final
 * rect IS deleting the room — the same effect as the Delete button);
 * - **delete room** → `remove_room`.
 *
 * Every edit rides the command bus, so it's synced + undoable. The volume readout reuses the pure
 * domain helpers ({@link roomFloorCells} / {@link roomVolume}), never recomputed here.
 */
export function RoomInspectorBody({
  room,
  actorId,
  disabled,
  dispatch,
  isGm,
}: {
  room: Room | null;
  actorId: string;
  disabled: boolean;
  dispatch: EncounterDispatch;
  /**
   * The capability prop (`CLAUDE.md` "UI mirrors the reducer's rules as affordances", the
   * `canEditBase`/`canConceal`/`canManageTurns` convention above) — `set_room`/`remove_room` are
   * GM-only in `canIssueEncounter`, so all three write controls sit behind this capability —
   * without it a player gets a live `✕ Delete room`.
   * **Hidden, not disabled** for a non-GM viewer — `off`
   * below stays a pure connectivity gate (`disabled`/`!actorId`), a different axis; don't conflate
   * the two. The identity block (name / cells / m³) stays visible either way — it's
   * already painted read-only on the board (`pixiCombatBoard.ts`'s `buildRoomContainer`).
   */
  isGm: boolean;
}) {
  if (!room) {
    return <div className="gms-content gms-content-message">This room is no longer in the encounter.</div>;
  }
  const off = disabled || !actorId;
  const removeRect = (i: number) => {
    // Dropping the room's LAST rect deletes the room (a zero-rect room is meaningless) — same as Delete.
    if (room.rects.length <= 1) {
      dispatch({ type: "remove_room", actorId, roomId: room.id });
      return;
    }
    dispatch({ type: "set_room", actorId, room: { ...room, rects: room.rects.filter((_, j) => j !== i) } });
  };
  return (
    // Brutalist re-skin (combat-edit-inspector-brutalist) — the room twin of EditInspectorBody, same
    // `.npc-ctl-*` control surface. Behaviour untouched (rename / per-rect ✕ / delete-room), now
    // gated behind `isGm` (gm-only-affordance-leaks).
    <div className="gms-content npc-ctl">
      <div className="npc-ctl-id">
        <div className="npc-ctl-id-main">
          <span className="npc-ctl-name">{room.name}</span>
          <span className="npc-ctl-classrole">
            room · {roomFloorCells(room)}□ · {roomVolume(room)} m³
          </span>
        </div>
      </div>

      {isGm && (
        <NameField
          name={room.name}
          disabled={off}
          onCommit={(name) => dispatch({ type: "set_room", actorId, room: { ...room, name } })}
        />
      )}

      {isGm && (
        <div className="npc-ctl-edit-field">
          <span className="npc-ctl-label">Rects ({room.rects.length})</span>
          <div className="npc-ctl-rect-list">
            {room.rects.map((r: RoomRect, i: number) => (
              <div key={i} className="npc-ctl-rect">
                <span className="npc-ctl-rect-coords">
                  ({r.x},{r.y}) · {r.w}×{r.h}
                </span>
                <button
                  type="button"
                  onClick={() => removeRect(i)}
                  disabled={off}
                  aria-label="remove rect"
                  {...tipAttrs({ label: "Remove rect", hint: room.rects.length <= 1 ? "the last rect: deletes the room" : "take this rect out of the room" })}
                  className="npc-ctl-rect-x"
                >
                  <Icon icon={faTrashCan} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isGm && (
        <div className="npc-ctl-edit-actions">
          <button
            type="button"
            onClick={() => dispatch({ type: "remove_room", actorId, roomId: room.id })}
            disabled={off}
            title={`Delete ${room.name}`}
            className="npc-ctl-edit-delete"
          >
            <Icon icon={faTrashCan} className="icon-lead" />Delete room
          </button>
        </div>
      )}
    </div>
  );
}
